import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import ConversationHeader from './ConversationHeader';
import ConversationSidebar from './ConversationSidebar';
import Messages from './Messages';
import MessageComposer from './MessageComposer';
import { MessagesProvider } from './MessagesContext';
import PitchAsMessage from './PitchAsMessage';
import useIsDesktop from './useIsDesktop';
import FirstSendComposer from './FirstSendComposer';
import PitchUserActionsDropdown from '../my_requests/pitch_inbox/PitchUserActionsDropdown';
// V1-MIGRATION: V1's native Pangram button (V2's pane uses the views_shared one)
import AiDetectorButton from '../pitch/pangram/ai_detector_button';
import stripHtmlTags from './ConversationsList/Conversation/stripHtmlTags';
import { isFastPitch } from './fastPitch';

// Center pane, composed from the REAL my_inbox components ("follow the inbox").
// Replied pitch: the conversation exactly as MyInbox renders it. Unreplied: the same
// components fed a conversation-shaped object with the pitcher as sole participant.
// The .my-inbox class carries the shared inbox styles.
function PitchThreadPane({
  pitch,
  v2PitchId,
  conversation,
  request,
  onConversationCreated,
  onMarkRead,
  onActionComplete,
  onBack,
  muteReport = null,
  canReply = true,
  readProfileId = null,
}) {
  const isDesktop = useIsDesktop();
  // Design feedback: the profile side panel starts closed here (unlike the global inbox,
  // whose persisted preference this deliberately does not touch)
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const toggleSidebar = useCallback(() => setSidebarExpanded((prev) => !prev), []);

  // Staff can't reply for reporters: the page-level canReply flag (already honored by the
  // Table view's PitchCard and Reply All) renders the Inbox view composers inert too.
  const composerFor = (composer) => (canReply ? (
    composer
  ) : (
    <div title="Only reporters can reply to pitches" style={{ cursor: 'not-allowed' }}>
      <div style={{ opacity: 0.5, pointerEvents: 'none' }} aria-disabled="true">
        {composer}
      </div>
    </div>
  ));

  // Audited pitcher: same treatment as the Table view's PitchCard — warning banner, and
  // the not-yet-replied pitch stays hidden until "View Pitch". Once a conversation exists
  // the banner is caution-only (collapsing would hide the reporter's own replies too).
  const isAudit = !!(pitch.under_audit || pitch.user_under_audit_at);
  const [auditExpanded, setAuditExpanded] = useState(false);

  // Figma "Quick Reply": the Table view's fast-pitch warning, pane edition — one compact
  // line. Renders only where the request's creation time is known (the request page); the
  // messaging inbox passes a slug/title-only request shape, so it stays off there — which
  // also keeps the Check-for-AI sentence tied to surfaces that ship the detector.
  const fastBanner = isFastPitch(pitch.sent_at, request.created_at, pitch.pitch_text) && (
    <div className="pi-fast-banner flex-shrink-0 px-3 text-center paragraph-sm">
      <i className="fa-solid fa-circle-exclamation me-1" aria-hidden="true" />
      <strong>Wow that was fast!</strong> This pitch came within 15 minutes of your request. Sometimes a super-fast
      turnaround like this might mean AI was involved. To double-check we recommend using{' '}
      <AiDetectorButton text={stripHtmlTags(pitch.pitch_text || '')} pitchId={pitch.id} displayAsButton={false} />,
      powered by Pangram!
    </div>
  );

  const auditBanner = isAudit && (
    <div
      className={'pi-audit-banner flex-shrink-0 background-color-raspberry-80 text-white py-1 px-2 '
        + 'd-flex align-items-center justify-content-between'}
    >
      <span className="text-start">
        <i className="fa-solid fa-circle-exclamation text-white" aria-hidden="true" /> This user has been flagged
        by other media users &mdash; review pitch with caution.
      </span>
      {!conversation && (
        <button
          type="button"
          className="text-white text-decoration-none small ms-3 flex-shrink-0 bg-transparent border-0"
          aria-expanded={auditExpanded}
          onClick={() => setAuditExpanded((e) => !e)}
        >
          {auditExpanded ? 'Hide Pitch' : 'View Pitch'}{' '}
          <i className="fa-solid fa-chevron-right ms-1" aria-hidden="true" />
        </button>
      )}
    </div>
  );

  // Rendered under the pitch's request-title bar (design feedback), via the shared
  // components' optional banner slot
  // Same anatomy as the fast-pitch banner: a full-width strip in the pane's banner stack,
  // above the request-title bar (design polish 2026-09-03; was an alert under it).
  const spamBanner = pitch.spam_flagged && (
    <div className="pi-spam-banner flex-shrink-0 px-3 text-center paragraph-sm">
      <i className="fa-solid fa-circle-exclamation me-1" aria-hidden="true" />
      <strong>Flagged User</strong> — previously flagged by other Media users for SPAM and/or using AI in pitches.
      Proceed with caution: consider checking this pitch with a tool like{' '}
      <a href="https://pangram.com/" target="_blank" rel="noreferrer">Pangram</a>.
    </div>
  );

  // Stable identity: PitchAsMessage refetches when this object changes — a fresh literal
  // per render makes the pane flash on every unrelated section re-render.
  // V1-MIGRATION: PitchAsMessage fetches from V2, so it needs the V2 pitch id. The request
  // page passes v2PitchId explicitly (its pitch prop is a V1 record; the bridge sends null
  // while loading); the messaging inbox omits the prop — its rows are V2-native, so the
  // pitch's own id IS the V2 id. The id spaces overlap numerically, so no naive fallback.
  const resolvedV2PitchId = v2PitchId === undefined ? pitch.id : v2PitchId;
  const pitchOriginShape = useMemo(
    () => ({ origin_id: resolvedV2PitchId, origin_type: 'Interact::Pitch' }),
    [resolvedV2PitchId],
  );

  // Pre-conversation shape for header/sidebar/composer; the pseudo conversation_id only
  // satisfies prop shapes — nothing fetches by it.
  const pitcherAsConversation = useMemo(
    () => ({
      conversation_id: `pitch-${pitch.id}`,
      conversation_senders: [
        {
          slug: pitch.profile_slug,
          name: pitch.profile_name,
          first_name: (pitch.profile_name || '').split(' ')[0],
          profile_image_url: pitch.profile_image_url,
          title_at_workplace: pitch.profile_title_at_workplace,
          type_of_profile: pitch.type_of_profile,
        },
      ],
      conversation_receivers: [],
    }),
    [pitch],
  );

  const sidebar = (shape) => sidebarExpanded && (
      <div className="sidebar-pane d-flex flex-column overflow-auto flex-shrink-0 bg-white">
        <ConversationSidebar conversation={shape} />
      </div>
    );

  if (!conversation) {
    return (
      <div className="pi-thread my-inbox">
        <ConversationHeader
          conversation={pitcherAsConversation}
          onBack={onBack}
          sidebarExpanded={sidebarExpanded}
          onToggleSidebar={toggleSidebar}
          dropdownOverride={<PitchUserActionsDropdown pitch={pitch} muteReport={muteReport} />}
          pitchedEntities={pitch.pitched_entities}
        />
        {fastBanner}
        {spamBanner}
        {auditBanner}
        <div className="conversation-body d-flex flex-grow-1 overflow-hidden">
          <div className="messages-column d-flex flex-column flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
            {/* Messages' scroll-area classes, so PitchAsMessage geometry matches both states */}
            <div className="d-flex flex-column flex-grow-1 overflow-y-auto overflow-x-hidden position-relative">
              <div className="d-flex flex-column flex-shrink-0">
                {resolvedV2PitchId && (!isAudit || auditExpanded) && (
                  <PitchAsMessage conversation={pitchOriginShape} />
                )}
              </div>
            </div>
            {composerFor(
              <FirstSendComposer
                pitch={pitch}
                request={request}
                conversationShape={pitcherAsConversation}
                onSent={onConversationCreated}
              />,
            )}
          </div>
          {sidebar(pitcherAsConversation)}
        </div>
      </div>
    );
  }

  return (
    // MyInbox parity: desktop clicks in the thread mark it read (mobile taps stay exploratory)
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- capture-phase convenience, mirrors MyInbox
    <div
      className="pi-thread my-inbox"
      onClickCapture={() => {
        if (isDesktop) onMarkRead?.(conversation.conversation_id);
      }}
    >
      <ConversationHeader
        conversation={conversation}
        onReloadConversation={onActionComplete}
        onActionComplete={onActionComplete}
        onBack={onBack}
        sidebarExpanded={sidebarExpanded}
        onToggleSidebar={toggleSidebar}
        dropdownOverride={<PitchUserActionsDropdown pitch={pitch} muteReport={muteReport} />}
        pitchedEntities={pitch.pitched_entities}
      />
      {fastBanner}
      {spamBanner}
      {auditBanner}
      <MessagesProvider
        conversationId={conversation.conversation_id}
        readProfileId={readProfileId}
        onMarkRead={() => onMarkRead?.(conversation.conversation_id)}
      >
        <div className="conversation-body d-flex flex-grow-1 overflow-hidden">
          <div className="messages-column d-flex flex-column flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
            <Messages conversation={conversation} />
            {composerFor(<MessageComposer conversation={conversation} />)}
          </div>
          {sidebar(conversation)}
        </div>
      </MessagesProvider>
    </div>
  );
}

PitchThreadPane.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types -- server-serialized config passed through
  muteReport: PropTypes.object,
  v2PitchId: PropTypes.number,
  canReply: PropTypes.bool,
  // Whose receipts to read the thread under; defaults to the viewer's own profile
  readProfileId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pitch: PropTypes.shape({
    id: PropTypes.number.isRequired,
    profile_name: PropTypes.string,
    profile_slug: PropTypes.string,
    profile_image_url: PropTypes.string,
    profile_workplace: PropTypes.string,
    profile_title_at_workplace: PropTypes.string,
    type_of_profile: PropTypes.string,
    under_audit: PropTypes.bool,
    spam_flagged: PropTypes.bool,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types -- conversations-view row passed to MyInbox components verbatim
  conversation: PropTypes.object,
  request: PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
  onConversationCreated: PropTypes.func.isRequired,
  onMarkRead: PropTypes.func,
  onActionComplete: PropTypes.func,
  onBack: PropTypes.func,
};

export default PitchThreadPane;
